import type { RecentStudyType } from '@mathflat/domain/StudentHome'
import { studentHomeApi } from '@mathflat/domain/StudentHome'
import { format } from 'date-fns'
import { makeAutoObservable, runInAction } from 'mobx'

import { getDefaultStudyPeriodRange } from '~/@common/utils/studyPeriod'

import RecentStudy from '../@common/model/RecentStudy'

type DateRange = { startDate: string; endDate: string }

/** 학부모 학습홈 — 학습 카드 서비스  */
export default class ParentStudyHistoryService {
  private _recentStudies: RecentStudy[] = []
  private _filterType: RecentStudyType = 'ALL'
  private _dateRange: DateRange
  private _isReady = false
  // 직전 요청 취소용 — 빠른 기간/자녀/탭 전환 시 stale 응답이 최신 state를 덮어쓰지 않도록.
  private _abortController: AbortController | null = null

  constructor() {
    const { start, end } = getDefaultStudyPeriodRange()
    this._dateRange = {
      startDate: format(start.dateObj, 'yyyy-MM-dd'),
      endDate: format(end.dateObj, 'yyyy-MM-dd'),
    }
    makeAutoObservable(this)
  }

  get isReady() {
    return this._isReady
  }
  get filterType() {
    return this._filterType
  }
  get dateRange() {
    return this._dateRange
  }

  /** 백엔드가 materialViewType으로 이미 필터링한 결과를 그대로 반환 */
  get recentStudies(): RecentStudy[] {
    return this._recentStudies
  }

  setFilterType(type: RecentStudyType) {
    this._filterType = type
  }
  setDateRange(range: DateRange) {
    this._dateRange = range
  }

  reset() {
    this._recentStudies = []
    this._filterType = 'ALL'
    this._isReady = false
  }

  async load(studentId: string) {
    // 진행 중이던 이전 요청은 취소
    this._abortController?.abort()
    const controller = new AbortController()
    this._abortController = controller
    const { signal } = controller

    runInAction(() => {
      this._recentStudies = []
      this._isReady = false
    })

    try {
      const items = await studentHomeApi.getParentRecentStudies(
        {
          studentId,
          startDate: this._dateRange.startDate,
          endDate: this._dateRange.endDate,
          materialViewType: this._filterType,
        },
        signal,
      )

      // 응답이 돌아온 시점에 이미 다른 요청으로 교체되었으면 반영하지 않음
      if (signal.aborted || this._abortController !== controller) return

      runInAction(() => {
        this._recentStudies = items.map((dto) => new RecentStudy(dto))
        this._isReady = true
      })
    } catch (err) {
      if (signal.aborted) return // abort로 인한 실패는 무시
      throw err
    }
  }
}
